///
/// EarthBrowser
///
/// FrontNode - Renders one weather front as a colored line draped on the
/// globe with extruded 3D glyphs along it: blue triangles pointing toward
/// the warm air for cold fronts, red semicircles pointing toward the cold
/// air for warm fronts, alternating triangle/semicircle pairs on opposite
/// sides for stationary fronts, and purple triangle/semicircle pairs on
/// the same side for occluded fronts (standard chart conventions).
///
/// Glyph size and spacing are held near-constant on screen: a fixed pool
/// of glyph slots is allocated up front and, each frame, the number in
/// use and their scale follow the camera distance (updateScreenSpace), so
/// fronts read the same from space and from a regional view.
///
/// Front polylines arrive with a fixed vertex count and consistent
/// orientation (warm air on the right of travel), so matched fronts can be
/// morphed between timesteps via updateInterpolated().
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///

import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { TextRenderer } from "@/earthbrowser/entities/TextNode/TextRenderer"
import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { rgba, type color } from "@stovokor/math/color"
import { matrix } from "@stovokor/math/matrix"
import { trig } from "@stovokor/math/math"
import { cross, normal, type vec3 } from "@stovokor/math/vector"
import { SimpleMaterial } from "@stovokor/shader/Material"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type FrontFeature, type FrontPoint } from "../weather-grid/FrontDetector"
import { FrontGlyphGeometry } from "./FrontGlyphGeometry"

const cColdColor: color = rgba(0.25, 0.55, 1.0, 0.9)
const cWarmColor: color = rgba(1.0, 0.3, 0.25, 0.9)
const cOccludedColor: color = rgba(0.72, 0.4, 0.95, 0.9)
const cLineWidth = 2.5
// Below the H/L markers (5 km), above the isoline sphere
const cAltitudeKm = 4
// Stationary fronts alternate line colors in chunks of this many vertices
const cStationaryChunk = 8
// Glyph geometry is built at this size; on-screen size comes from scaling
const cGlyphWidthKm = 130
const cGlyphHeightKm = 80
const cGlyphDepthKm = 8
// Screen-space targets: glyph width and along-front spacing in pixels,
// held while the camera is within the distance cap, world-fixed beyond it
const cTargetGlyphPx = 15
const cTargetSpacingPx = 70
const cMaxScaleDistanceKm = 8000
const cMinGlyphs = 2
const cMaxGlyphs = 24
// Interpolated moves smaller than this on screen are not applied
const cMinVisibleMovePx = 0.25

interface LineRun {
	node: PolylineNode
	start: number
	end: number  // inclusive vertex range into the fixed-count points
	baseColor: color
}

interface GlyphSlot {
	node: NodeBase
	/** +1 = toward warm air (right of travel), -1 = toward cold air */
	side: 1 | -1
}

export class FrontNode extends NodeBase {
	public readonly front: FrontFeature
	private readonly lines: LineRun[] = []
	private readonly glyphs: GlyphSlot[] = []
	private triangleGeometry?: FrontGlyphGeometry
	private semicircleGeometry?: FrontGlyphGeometry
	private readonly coldMaterial = new SimpleMaterial(cColdColor)
	private readonly warmMaterial = new SimpleMaterial(cWarmColor)
	private readonly occludedMaterial = new SimpleMaterial(cOccludedColor)
	private lastAlpha = 1
	private positions: vec3[] = []
	private activeGlyphs = cMaxGlyphs
	private glyphScale = 1
	private glyphsDirty = true
	// km per screen pixel at the front, from the last updateScreenSpace;
	// 0 until then so the first interpolation always applies
	private kmPerPx = 0

	constructor(context: StovokorContext, front: FrontFeature) {
		super()
		this.front = front
		this.coldMaterial.transparency = true
		this.warmMaterial.transparency = true
		this.occludedMaterial.transparency = true

		const n = front.points.length
		if (n < 2) return

		this.positions = front.points.map(toWorldPosition)

		// --- Lines ---
		if (front.type === 'stationary') {
			for (let start = 0, i = 0; start < n - 1; start += cStationaryChunk, i++) {
				const end = Math.min(start + cStationaryChunk, n - 1)
				this.addLineRun(context, this.positions, start, end, i % 2 === 0 ? cColdColor : cWarmColor)
			}
		} else {
			this.addLineRun(context, this.positions, 0, n - 1, lineColor(front.type))
		}

		// --- Glyph pool (slot type by parity so alternation survives any count) ---
		for (let i = 0; i < cMaxGlyphs; i++) {
			const alternating = front.type === 'stationary' || front.type === 'occluded'
			const isTriangle = front.type === 'cold' || (alternating && i % 2 === 0)
			// Triangles point toward warm air and semicircles toward cold,
			// except occluded pairs which both point the way the front moves
			const side: 1 | -1 = front.type === 'occluded' ? 1 : (isTriangle ? 1 : -1)
			const geometry = isTriangle ? this.getTriangleGeometry(context) : this.getSemicircleGeometry(context)
			const material = front.type === 'occluded'
				? this.occludedMaterial
				: (isTriangle ? this.coldMaterial : this.warmMaterial)

			const glyphNode = new GlyphNode(new TextRenderer(context, geometry, material))
			this.glyphs.push({ node: glyphNode, side })
			this.addNode(glyphNode)
		}

		this.updateGlyphTransforms()
	}

	private addLineRun(
		context: StovokorContext,
		positions: vec3[],
		start: number,
		end: number,
		baseColor: color
	): void {
		if (end - start < 1) return
		const node = new PolylineNode(context, {
			positions: positions.slice(start, end + 1),
			width: cLineWidth,
			color: baseColor,
			depthWrite: false,
		})
		this.lines.push({ node, start, end, baseColor })
		this.addNode(node)
	}

	private getTriangleGeometry(context: StovokorContext): FrontGlyphGeometry {
		if (!this.triangleGeometry) {
			this.triangleGeometry = new FrontGlyphGeometry(context, 'triangle', cGlyphWidthKm, cGlyphHeightKm, cGlyphDepthKm)
		}
		return this.triangleGeometry
	}

	private getSemicircleGeometry(context: StovokorContext): FrontGlyphGeometry {
		if (!this.semicircleGeometry) {
			this.semicircleGeometry = new FrontGlyphGeometry(context, 'semicircle', cGlyphWidthKm, cGlyphHeightKm, cGlyphDepthKm)
		}
		return this.semicircleGeometry
	}

	/**
	 * Reposition the front along interpolated points (same fixed vertex count)
	 * and apply a fade alpha. Cheap enough to call every frame: polyline GPU
	 * buffers are refilled in place and glyph transforms recomputed.
	 */
	public updateInterpolated(points: FrontPoint[], alpha: number): void {
		// Skip the polyline refill and glyph rebuild while no point has moved
		// a visible distance from what is already applied: at live speed the
		// interpolation creeps by metres per frame, and re-uploading every
		// frame for that kept the frame loop awake
		const positions = points.map(toWorldPosition)
		if (positions.length === this.positions.length && this.positions.length > 0) {
			const threshold = cMinVisibleMovePx * this.kmPerPx
			let moved = false
			for (let i = 0; i < positions.length; i++) {
				if (positions[i].subtract(this.positions[i]).length > threshold) {
					moved = true
					break
				}
			}
			if (moved) {
				this.applyPositions(positions)
			}
		} else {
			this.applyPositions(positions)
		}

		if (alpha !== this.lastAlpha) {
			this.lastAlpha = alpha
			for (const line of this.lines) {
				const c = line.baseColor
				line.node.updateColor(rgba(c.r, c.g, c.b, c.a * alpha))
			}
			this.coldMaterial.color = rgba(cColdColor.r, cColdColor.g, cColdColor.b, cColdColor.a * alpha)
			this.warmMaterial.color = rgba(cWarmColor.r, cWarmColor.g, cWarmColor.b, cWarmColor.a * alpha)
			this.occludedMaterial.color = rgba(cOccludedColor.r, cOccludedColor.g, cOccludedColor.b, cOccludedColor.a * alpha)
		}
	}

	private applyPositions(positions: vec3[]): void {
		this.positions = positions
		for (const line of this.lines) {
			line.node.updatePositions(positions.slice(line.start, line.end + 1))
		}
		this.glyphsDirty = true
	}

	/**
	 * Size and space the glyphs for the camera: constant on-screen width
	 * and spacing while close, shrinking with the globe once far away.
	 * @param cameraPos camera position in world space (km)
	 * @param worldPerPixelAtUnitDist world units per screen pixel at distance 1
	 */
	public updateScreenSpace(cameraPos: vec3, worldPerPixelAtUnitDist: number): void {
		const n = this.positions.length
		if (n < 2) return

		const mid = this.positions[n >> 1]
		const trueDistance = cameraPos.subtract(mid).length
		this.kmPerPx = trueDistance * worldPerPixelAtUnitDist
		const distance = Math.min(trueDistance, cMaxScaleDistanceKm)
		const kmPerPx = distance * worldPerPixelAtUnitDist

		const scale = (cTargetGlyphPx * kmPerPx) / cGlyphWidthKm
		const spacingKm = cTargetSpacingPx * kmPerPx
		const count = Math.min(cMaxGlyphs, Math.max(cMinGlyphs, Math.round(this.front.lengthKm / spacingKm)))

		if (Math.abs(scale - this.glyphScale) > this.glyphScale * 0.002 || count !== this.activeGlyphs) {
			this.glyphScale = scale
			this.activeGlyphs = count
			this.glyphsDirty = true
		}
		if (this.glyphsDirty) {
			this.updateGlyphTransforms()
		}
	}

	/** Place the active glyphs on the sphere, base along the line, pointing sideways */
	private updateGlyphTransforms(): void {
		this.glyphsDirty = false
		const positions = this.positions
		const n = positions.length
		if (n < 2) return

		const count = this.activeGlyphs
		const s = this.glyphScale
		for (let i = 0; i < this.glyphs.length; i++) {
			const glyph = this.glyphs[i]
			glyph.node.visible = i < count
			if (i >= count) continue

			const f = ((i + 0.5) / count) * (n - 1)
			const i0 = Math.min(Math.floor(f), n - 2)
			const t = f - i0

			const a = positions[i0]
			const b = positions[i0 + 1]
			const p = a.add(b.subtract(a).multiply(t))
			const up = normal(p)

			// Tangent along travel direction, projected onto the surface plane
			const chord = b.subtract(a)
			const tangent = normal(chord.subtract(up.multiply(dot3(chord, up))))
			// Right of travel = toward the warm air (by polyline orientation)
			const pointDir = cross(tangent, up).multiply(glyph.side)
			const zAxis = cross(tangent, pointDir)

			glyph.node.transform = new matrix([
				tangent.x * s, tangent.y * s, tangent.z * s, 0,
				pointDir.x * s, pointDir.y * s, pointDir.z * s, 0,
				zAxis.x * s, zAxis.y * s, zAxis.z * s, 0,
				p.x, p.y, p.z, 1,
			])
		}
	}

	override destroy(): void {
		this.triangleGeometry?.destroy()
		this.semicircleGeometry?.destroy()
		super.destroy()
	}
}

function lineColor(type: FrontFeature["type"]): color {
	switch (type) {
		case 'cold': return cColdColor
		case 'warm': return cWarmColor
		case 'occluded': return cOccludedColor
		default: return cColdColor
	}
}

/** NodeBase whose protected renderer can be assigned at construction */
class GlyphNode extends NodeBase {
	constructor(renderer: TextRenderer) {
		super()
		this.renderer = renderer
	}
}

function dot3(a: vec3, b: vec3): number {
	return a.x * b.x + a.y * b.y + a.z * b.z
}

function toWorldPosition(point: FrontPoint): vec3 {
	const geo = new geographic(point.lon * trig.rad, point.lat * trig.rad, 0)
	return geo.normal.multiply(wgs.a + cAltitudeKm)
}
